import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/auth/auth.service';
import {
  NotificationBranding,
  CreateNotificationBrandingRequest,
  UpdateNotificationBrandingRequest,
  UploadLogoResponse,
} from '../models/notification-branding.model';

@Injectable({
  providedIn: 'root',
})
export class NotificationBrandingService {
  private readonly apiUrl = `${environment.cafler_backoffice_back_url}/v1`;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}


  getBranding(): Observable<NotificationBranding | null> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          return of(null);
        }
        return this.http.get<NotificationBranding>(
          `${this.apiUrl}/center-groups/notification-branding`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }),
      catchError((error) => {
        if (error.status === 404) {
          return of(null);
        }
        console.error('[NotificationBrandingService] Error fetching branding:', error);
        return of(null);
      })
    );
  }

  createBranding(request: CreateNotificationBrandingRequest): Observable<NotificationBranding | null> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          return of(null);
        }
        return this.http.post<NotificationBranding>(
          `${this.apiUrl}/center-groups/notification-branding`,
          request,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }),
      catchError((error) => {
        console.error('[NotificationBrandingService] Error creating branding:', error);
        throw error;
      })
    );
  }

  updateBranding(request: UpdateNotificationBrandingRequest): Observable<NotificationBranding | null> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          return of(null);
        }
        return this.http.put<NotificationBranding>(
          `${this.apiUrl}/center-groups/notification-branding`,
          request,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }),
      catchError((error) => {
        console.error('[NotificationBrandingService] Error updating branding:', error);
        throw error;
      })
    );
  }

  deleteBranding(): Observable<boolean> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          return of(false);
        }
        return this.http.delete(
          `${this.apiUrl}/center-groups/notification-branding`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        ).pipe(
          switchMap(() => of(true))
        );
      }),
      catchError((error) => {
        console.error('[NotificationBrandingService] Error deleting branding:', error);
        return of(false);
      })
    );
  }

  saveBranding(
    branding: NotificationBranding | null,
    request: CreateNotificationBrandingRequest | UpdateNotificationBrandingRequest
  ): Observable<NotificationBranding | null> {
    if (branding?.id) {
      return this.updateBranding(request);
    }
    return this.createBranding(request);
  }

  uploadLogo(file: File, type: 'logo' | 'footerLogo' = 'logo'): Observable<UploadLogoResponse | null> {
    const formData = new FormData();
    formData.append('file', file);

    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          return of(null);
        }
        return this.http.post<UploadLogoResponse>(
          `${this.apiUrl}/center-groups/notification-branding/logo?type=${type}`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }),
      catchError((error) => {
        console.error('[NotificationBrandingService] Error uploading logo:', error);
        throw error;
      })
    );
  }

  isValidHexColor(color: string): boolean {
    if (!color) return true;
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
  }

  // =============================================================================
  // MOCK DATA - COMMENTED OUT (Now using real API endpoints)
  // =============================================================================
  /*
  private getDefaultBranding(): NotificationBranding {
    return {
      id: 0,
      centerGroupId: 1,
      primaryColor: '#1e3a8a',
      secondaryColor: '#3b82f6',
      accentColor: '#10b981',
      textColor: '#1f2937',
      backgroundColor: '#ffffff',
      logoUrl: '',
      footerLogoUrl: '',
      fontFamily: 'Arial, sans-serif',
      companyName: 'Mi Taller',
      contactEmail: 'contacto@mitaller.com',
      contactPhone: '+34 900 000 000',
      address: 'Calle Principal 123, 28001 Madrid',
      websiteUrl: 'https://www.mitaller.com',
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }
  */
}
